module Meevo.Plugins.Tippy {

    export enum TippyMode {
        Disabled,
        Offline,
        Kiosk
    }

    interface IEmployeeTipChargeAmounts { [key: string]: number };

    interface IPartialRefundResponse {
        data: {
            tipLines: {
                employeeId: string,
                convenienceFee: number
                transactionId: string
            }[]
        }
    }

    export interface IUnauthorizedConfig {
        additionalMessage?: string,
        modalType?: Meevo.Components.Modal.ModalTypeEnum
    }

    export class TippyService {
        registerContext: Register.RegisterContext;
        myTimeout: any;
        kiosk: Kiosk;

        constructor(private dataService: Meevo.Components.Common.TippyDataService,
            private $q: ng.IQService,
            private modalService: Meevo.Components.Modal.IModalService,
            private pushMessageHandlerService: Meevo.Components.PushMessageHandlerService,
            private securityService: Meevo.Components.Common.ISecurityService,
            private roundingService: Meevo.Components.Common.IRoundingService,
            private $translate: angular.translate.ITranslateService,
            private identityService: Meevo.Components.Common.IIdentityService
        ) {
        }

        public getTippyMode(drawerId: string): ng.IPromise<TippyMode> {
            return this.checkModule().then(isModuleEnabled => {
                if (isModuleEnabled) {
                    let promises = [
                        this.dataService.getSection("", "config"),
                        this.dataService.getKioskByDrawerId(drawerId)
                    ];

                    return this.$q.all(promises).then(results => {
                        this.kiosk = results[1];

                        if (results[0].isTipProcessingEnabled && !results[1]) {
                            return TippyMode.Offline;
                        }

                        return results[0].isTipProcessingEnabled && !!results[1] ?
                            TippyMode.Kiosk :
                            TippyMode.Disabled;
                    })
                }
                else {
                    return TippyMode.Disabled;
                }
            });
        }

        createTransaction(registerContext: Register.RegisterContext) {
            registerContext.sale.externalTipEmployeeIds = [];
            let tipLines = _(registerContext.sale.tipLines).where({ isRefund: false });
            if (tipLines.length === 0 ||
                registerContext.registerTicketService.isPastTransactionMode) {

                return this.$q.resolve();
            }

            const tipEmployeeIds = _(registerContext.sale.tipLines).pluck('employeeId');
            this.registerContext = registerContext;

            let unauthorizedConfig: IUnauthorizedConfig = {
                additionalMessage: "MESSAGE_TIPPY_UNAUTHORIZED_CREATE_TRANSACTION"
            }
            return this.isTippyKioskMode(registerContext.selectedDrawer.id, tipEmployeeIds, unauthorizedConfig).then(isKioskMode => {
                if (!isKioskMode) {
                    return;
                }

                let employeeChargeAmounts = this.getEmployeeTipChargeAmounts();
                let request = {
                    tipLines: this.getTipLines(employeeChargeAmounts),
                    clients: this.getClients(),
                    isFundedAtSettlementTime: registerContext.hasMeevoPaySplitFunding()
                };

                this.setTippyPurchasesAndTaxes(registerContext.sale, employeeChargeAmounts, request);

                return this.dataService.customMethodData(`kiosk/${this.kiosk.deviceId}/transaction?enableUnauthorizedHandling=true`, "POST", request).then((response) => {
                    if (!response.data) {
                        return;
                    }

                    if (!response.data.transactionId && response.data.isKioskLocked) {
                        this.modalService.alert('MESSAGE_TIPPY_KIOSK_LOCKED', Components.Modal.ModalTranslationModes.Translate);
                        return this.$q.reject();
                    }

                    this.setTransactionTimer();

                    this.registerContext.sale.externalTipTransactionId = response.data.transactionId;

                    let options: Meevo.Components.Modal.IModalOptions = {
                        templateUrl: "app/plugins/register/modals/tippyBusyModal.template.html",
                        controller: "meevo.plugins.register.TippyBusyModal",
                        controllerAs: "vm",
                        bindToController: true
                    }

                    return this.modalService.showCustom(options).catch(() => {
                        this.cancelTransaction();
                        registerContext.selectSection(Register.RegisterSectionTypeEnum.Services);
                        return this.$q.reject();
                    });
                }).catch((error) => {
                    if (!error.data || !error.data.skipInterceptor) {
                        return this.$q.reject(error.data.message);
                    }
                    if (error.data.customState && error.data.customState.isUnauthorized) {
                        let message = this.getCustomErrorMessage(error.data.message, unauthorizedConfig);
                        this.modalService.alert(message, Components.Modal.ModalTranslationModes.AsHtml);
                    }
                    else {
                        const modalOptions = {
                            okButtonContentKey: "LABEL_RETRY",
                            cancelButtonContentKey: "LABEL_CONTINUE",
                            showCancelButton: true
                        };
                        this.modalService.yellowConfirm(error.data.message,
                            Components.Modal.ModalTranslationModes.Translate, true, modalOptions
                        ).then(() => {
                            return this.createTransaction(registerContext);
                        }).catch(() => {
                            return this.$q.resolve();
                        });
                    }
                });
            });
        }

        private getTipLines(employeeChargeAmounts: IEmployeeTipChargeAmounts) {
            let tipLines = this.registerContext.sale.tipLines
                .filter((tipLine) => !tipLine.isRefund)
                .map((tipLine) => {
                    return {
                        employeeId: tipLine.employeeId,
                        amount: tipLine.amount,
                        serviceTotal: tipLine.serviceTotal,
                        prepaidTipMinimum: tipLine.prepaidTipMinimum,
                        isRefund: tipLine.isRefund
                    }
                });

            // package prepaid tip is not sent to MeevoTips
            for (const tipLine of tipLines) {
                tipLine.amount = this.registerContext.subtractNormalizedNumbers(tipLine.amount, tipLine.prepaidTipMinimum);
                tipLine.prepaidTipMinimum = 0;
            }

            for (const employeeId in employeeChargeAmounts) {
                if (employeeChargeAmounts.hasOwnProperty(employeeId)) {
                    let existingTipLine = _(tipLines).findWhere({ employeeId: employeeId, isRefund: false });
                    if (!!existingTipLine) {
                        existingTipLine.amount = this.registerContext.addNormalizedNumbers(existingTipLine.amount, employeeChargeAmounts[employeeId]);
                        existingTipLine.prepaidTipMinimum = this.registerContext.addNormalizedNumbers(existingTipLine.prepaidTipMinimum, employeeChargeAmounts[employeeId]);
                        existingTipLine.serviceTotal = this.registerContext.subtractNormalizedNumbers(existingTipLine.serviceTotal, employeeChargeAmounts[employeeId]);
                    }
                    else {
                        tipLines.push({
                            employeeId: employeeId,
                            amount: employeeChargeAmounts[employeeId],
                            prepaidTipMinimum: employeeChargeAmounts[employeeId],
                            serviceTotal: 0,
                            isRefund: false
                        })
                    }
                }
            }

            return tipLines;
        }

        private getClients() {
            let saleLines: any[] = [...this.registerContext.sale.saleLines];
            // if there's just tip lines but no sale lines, need to add dummy sale line so client is sent to tippy ms
            if (saleLines.length === 0 && this.registerContext.sale.tipLines.length > 0 &&
                this.registerContext.allUniqueClients.length > 0) {
                const firstClient = this.registerContext.allUniqueClients[0];
                saleLines.push({
                    clientId: firstClient.getId(),
                    client: firstClient.client
                });
            }
            return _(saleLines)
                .map((saleLine) => {
                    return {
                        employeeId: saleLine.employeeId,
                        clientId: saleLine.clientId,
                        firstName: !!saleLine.client ? saleLine.client.firstName : "",
                        lastName: !!saleLine.client ? saleLine.client.lastName : ""
                    }
                });
        }

        confirmTransaction(sale: Model.Sale) {
            if (!sale.externalTipTransactionId) {
                return;
            }

            this.dataService.customMethodData(`transaction/${sale.externalTipTransactionId}/confirm`, "POST",
                { meevoTransactionId: sale.transactionId });
            sale.externalTipTransactionId = null;
        }

        voidTransaction(externalTipTransactionId: string) {
            if (!externalTipTransactionId) {
                return;
            }

            this.dataService.customMethodData(`transaction/${externalTipTransactionId}/refund`, "POST", {});
        }

        refundTransaction(sale: Model.Sale, registerContext: Register.RegisterContext): ng.IPromise<boolean> {
            const employees: { amount: number, convenienceFee: number, employeeId: string, externalTipTransactionId: string }[] = [];

            for (let saleLine of sale.saleLines.filter(saleLine => !!saleLine.externalTipTransactionId && saleLine.isRefund)) {
                employees.push(...saleLine.charges.filter(charge => charge.chargeType == Model.ChargeTypeEnum.EmployeeTip).
                    map(charge => {
                        return {
                            amount: charge.finalAmount,
                            convenienceFee: 0,
                            employeeId: saleLine.employeeId,
                            externalTipTransactionId: saleLine.externalTipTransactionId
                        };
                }));
            }

            employees.push(...sale.tipLines.filter(tipLine => !!tipLine.externalTipTransactionId && tipLine.isRefund).
                map(tipLine => {
                    return {
                        amount: registerContext.subtractNormalizedNumbers(tipLine.amount, tipLine.prepaidTipMinimum),
                        convenienceFee: tipLine.convenienceFee,
                        employeeId: tipLine.employeeId,
                        externalTipTransactionId: tipLine.externalTipTransactionId
                    }
                }));

            if (employees.length === 0) {
                return this.$q.resolve(true);
            }

            const externalTipTransactionId = employees[0].externalTipTransactionId;
            const request = {
                meevoTransactionId: sale.transactionId,
                employees
            }
            
            this.dataService.customMethodData(`transaction/${externalTipTransactionId}/refund`, "POST", request);
            return this.dataService.customMethodData(`transaction/${externalTipTransactionId}`, "GET", {}).then((response) => {
                return response.data.isFundedAtSettlementTime;
            });
        }

        partialRefundTransaction(registerContext: Register.RegisterContext): ng.IPromise<any> {
            let refundExternalTipLines = registerContext.sale.tipLines.filter(tipLine => {
                return tipLine.isRefund &&
                    !!tipLine.externalTipTransactionId
            });

            registerContext.sale.tipConvenienceFeeRefund = 0;
            if (refundExternalTipLines.length === 0) {
                registerContext.updateTip();
                return this.$q.resolve();
            }

            let refundExternalTipTransactionIds = _(refundExternalTipLines).chain()
                .pluck("externalTipTransactionId")
                .uniq()
                .value();

            registerContext.busyService.start();

            return this.dataService.customMethodData(`refundInfo?transactionIds=${refundExternalTipTransactionIds}`, "GET", {}).then((response: IPartialRefundResponse) => {
                for (let refundTipLine of refundExternalTipLines) {
                    let matchingTransTipLine = response.data.tipLines.find(responseTipLine => {
                        return responseTipLine.employeeId === refundTipLine.employeeId &&
                            responseTipLine.transactionId === refundTipLine.externalTipTransactionId
                    });

                    if (!!matchingTransTipLine) {
                        refundTipLine.convenienceFee = matchingTransTipLine.convenienceFee;
                    }
                }
                this.updateTipConvenienceFee(registerContext.sale, registerContext, registerContext.sale.tipConvenienceFee);
            }).finally(() => {
                registerContext.busyService.stop();
                registerContext.updateTip();
            });
        }

        deleteTransaction(sale: Model.Sale) {
            if (!sale || !sale.externalTipTransactionId) {
                return;
            }

            this.dataService.customMethodData(`transaction/${sale.externalTipTransactionId}`, "DELETE", {});
            sale.externalTipTransactionId = null;
        }

        tipsSelected(transactionId) {
            if (this.registerContext.sale.externalTipTransactionId !== transactionId) {
                return;
            }
            this.getTransaction()
                .then(transaction => {
                    if (transaction.state === TransactionStateEnum.Canceled) {
                        // cancellation cleanup already occurred
                        return;
                    }
                    this.cleanupTransaction();
                });
        }

        private setTransactionTimer() {
            if (this.pushMessageHandlerService.isOpen) {
                return;
            }

            this.myTimeout = setTimeout(() => {
                this.getTransaction().then(transaction => {
                    if (transaction.state === TransactionStateEnum.Initial) {
                        this.setTransactionTimer();
                    }
            }) }, window["TippyPollingInterval"]);
        }

        private getTransaction(cleanup = true) : ng.IPromise<Transaction> {
            return this.dataService.customMethodData(`transaction/${this.registerContext.sale.externalTipTransactionId}`, "GET", {}).then((response) => {
                let transaction : Transaction = response.data;

                if (!transaction || transaction.state === TransactionStateEnum.Canceled) {
                    // transaction was externally cancelled
                    this.cleanupTransaction(true);
                    return transaction;
                }

                if (transaction.state === TransactionStateEnum.Pending) {
                    let employeeChargeAmounts = this.getEmployeeTipChargeAmounts();

                    for (let tipLine of transaction.tipLines) {
                        if (tipLine.amount > 0) {
                            this.registerContext.sale.externalTipEmployeeIds.push(tipLine.employeeId);
                        }
                        let matchingSaleTipLine = this.registerContext.sale.tipLines.find(saleTipLine => {
                            return saleTipLine.employeeId === tipLine.employeeId &&
                                saleTipLine.isRefund === false;
                        })
                        if (matchingSaleTipLine) {
                            matchingSaleTipLine.externalAmount = tipLine.amount;
                            // reset tipline amount to MeevoTips amount minus employee tip charge amount
                            matchingSaleTipLine.amount = this.registerContext.subtractNormalizedNumbers(tipLine.amount, employeeChargeAmounts[tipLine.employeeId] || 0);
                            // add package prepaid tip back to tipline amount
                            matchingSaleTipLine.amount = this.registerContext.addNormalizedNumbers(matchingSaleTipLine.amount, matchingSaleTipLine.prepaidTipMinimum);
                            if (tipLine.amount > 0) {
                                matchingSaleTipLine.externalTipTransactionId = this.registerContext.sale.externalTipTransactionId;
                            }
                            this.registerContext.sale.tipPercentage = 0;
                            matchingSaleTipLine.percentage = 0;
                        }
                    }

                    this.updateTipConvenienceFee(this.registerContext.sale, this.registerContext, response.data.convenienceFee);
                    this.registerContext.sale.tipAmount = undefined;
                    return this.registerContext.recalculateSale().then(() => {
                        return transaction;
                    }).finally(() => {
                        if (cleanup) {
                            this.cleanupTransaction();
                        }
                    });
                }
                else {
                    return transaction;
                }
            });
        }

        private getEmployeeTipChargeAmounts(): IEmployeeTipChargeAmounts {
            const saleLinesAndCharges = _(this.registerContext.sale.saleLines).chain()
                .map(saleLine => {
                    return {
                        saleLine,
                        chargeAmount: +_(saleLine.charges).chain()
                            .filter(charge => charge.chargeType === Model.ChargeTypeEnum.EmployeeTip)
                            .pluck("finalAmount")
                            .reduce((sum, finalAmount) => this.registerContext.addNormalizedNumbers(+sum, finalAmount))
                            .value()
                    }
                })
                .filter(saleLineAndCharges => saleLineAndCharges.chargeAmount > 0)
                .value();
            const employeeChargeAmounts = {};

            let addEmployeeChargeAmount = (employeeId: string, chargeAmount: number) => {
                if (employeeId === Guid.empty.toString()) {
                    return;
                }
                let existChargeAmount = employeeChargeAmounts[employeeId] || 0;
                employeeChargeAmounts[employeeId] = this.registerContext.addNormalizedNumbers(existChargeAmount, chargeAmount);
            }

            for (const saleLineAndCharges of saleLinesAndCharges) {
                let saleLine = saleLineAndCharges.saleLine;
                if (!!saleLine.secondEmployeeId && !!saleLine.employeeSplitCommission) {
                    var firstEmployeeChargePercentage = saleLine.employeeSplitCommission === 1 ? .5 : saleLine.employeeSplitCommission;
                    var firstEmployeeCharge = this.registerContext.roundingService.multiplyNormalizedNumbers
                        (saleLineAndCharges.chargeAmount, firstEmployeeChargePercentage);
                    addEmployeeChargeAmount(saleLine.employeeId, firstEmployeeCharge);
                    addEmployeeChargeAmount(saleLine.secondEmployeeId,
                        this.registerContext.subtractNormalizedNumbers(saleLineAndCharges.chargeAmount, firstEmployeeCharge));
                }
                else {
                    addEmployeeChargeAmount(saleLine.employeeId, saleLineAndCharges.chargeAmount);
                }
            }

            return employeeChargeAmounts;
        }

        private cleanupTransaction(cancel: boolean = false) {
            if (cancel) {
                this.modalService.cancel();
            } else {
                this.modalService.hide();
            }
            clearTimeout(this.myTimeout);
        }

        public unlockKiosk(drawerId: string): ng.IPromise<any> {
            return this.dataService.unlockKiosk(drawerId);
        }

        private cancelTransaction() {
            // Make one last attempt to get transaction
            this.getTransaction(false).then(transaction => {

                if (transaction && transaction.state === TransactionStateEnum.Initial) {
                    this.deleteTransaction(this.registerContext.sale);
                }
            })
            .finally(() => {
                this.cleanupTransaction();
            });
        }

        setTippyPurchasesAndTaxes(sale: Model.Sale, employeeChargeAmounts: IEmployeeTipChargeAmounts, request): void {
            let totalTipCharge = _(Object.keys(employeeChargeAmounts)).chain()
                .map(key => employeeChargeAmounts[key])
                .reduce((total, chargeAmount) => this.registerContext.addNormalizedNumbers(total, chargeAmount), 0)
                .value();

            request.purchasesAndTaxes = this.registerContext.normalizeDecimalPlaces(sale.saleLines.map(saleLine => saleLine.finalPriceCharge)
                .reduce((total, next) => total += next, 0) + sale.tax - sale.taxIncluded - totalTipCharge);
            request.purchasesAndTaxesLabel = this.registerContext.$translate.instant('LABEL_PURCHASES_AND_TAXES');
        }

        public getTippyEmployeeMappings(unauthorizedConfig: IUnauthorizedConfig = undefined):ng.IPromise<any> {
            let defer = this.$q.defer();

            let unauthorizedHandler = (ex) => {
                if (unauthorizedConfig) {
                    let message = this.getCustomErrorMessage(ex.data.message, unauthorizedConfig);
                    let modalType = unauthorizedConfig.modalType || Components.Modal.ModalTypeEnum.Alert;
                    if (modalType === Components.Modal.ModalTypeEnum.Alert) {
                        this.modalService.alert(message, Components.Modal.ModalTranslationModes.AsHtml, true);
                        defer.resolve([]);
                    }
                    else {
                        ex.message = message;
                        defer.reject(ex)
                    }
                }
                else {
                    defer.resolve([]);
                }
            };

            this.isTippyConfigured().then(isConfigured => {
                if (!isConfigured) {
                    defer.resolve([]);
                    return;
                }

                return this.dataService.customMethodData('Employees/List?enableUnauthorizedHandling=true', 'GET', null)
                    .then(res => {
                        defer.resolve(res.data ? res.data.mappings || [] : []);
                    })
                    .catch(ex => {
                        if (!ex.data || !ex.data.skipInterceptor) {
                            defer.reject(ex);
                            return;
                        }

                        unauthorizedHandler(ex);
                    });
            });

            return defer.promise;
        }

        public checkModule(): ng.IPromise<boolean> {
            if (this.identityService.inSandboxMode()) {
                return this.$q.resolve(false);
            }
            return this.securityService.checkModuleAccess(Meevo.Components.Common.SecurityKeys.INTEGRATION_TIPPY_INTERNAL)
                .then(res => res === Model.SecurityAccessEnum.Grant);
        }

        public isTippyConfigured(): ng.IPromise<boolean> {
            return this.checkModule().then(isModuleEnabled => {
                if (!isModuleEnabled) {
                    return false;
                }

                return this.dataService.getSection('', 'config').then(res => !!res && res.isTipProcessingEnabled && !!res.accountToken);
            });
        }

        public isTippyKioskMode(drawerId: string, tipEmployeeIds: string[], unauthorizedConfig: IUnauthorizedConfig = undefined): ng.IPromise<boolean> {
            return this.getTippyMode(drawerId).then(kioskMode => {
                if (kioskMode === TippyMode.Disabled) {
                    return false;
                }

                if (kioskMode === TippyMode.Kiosk) {
                    return true;
                }

                // Tippy Mode is OFFLINE
                return this.getTippyEmployeeMappings(unauthorizedConfig).then(mappings => {
                    const tippyEmployeeIds = _(mappings).pluck('meevoEmployeeId');
                    if (_(tipEmployeeIds).any(id => tippyEmployeeIds.indexOf(id) > -1)) {
                        return this.modalService.alert('MESSAGE_REGISTER_TIPPY_OFFLINE_MODE').then(() => false);
                    }

                    return false;
                });
            });
        }

        getKioskByDrawerId(drawerId: string): ng.IPromise<Model.TippyKiosk> {
            return this.checkModule().then(isGrant => {
                if (!isGrant) {
                    return undefined;
                }

                return this.dataService.getKioskByDrawerId(drawerId);
            });
        }

        hasExternalTip(sale: Model.Sale, excludeRefund: boolean = false) {
            return _(this.getExternalLines(sale)).any(tippyLine => !excludeRefund || !tippyLine.isRefund);
        }

        getExternalLines(sale: Model.Sale) {
            return [...sale.tipLines, ...sale.saleLines].filter(line => !!line.externalTipTransactionId);
        }

        hasExternalTipRefund(sale: Model.Sale) {
            return _(this.getExternalRefundLines(sale)).any();
        }

        getExternalRefundLines(sale: Model.Sale) {
            return this.getExternalLines(sale).filter(tippyLine => tippyLine.isRefund);
        }

        getExternalRefundTransactionId(sale: Model.Sale) {
            const refundLines = this.getExternalRefundLines(sale);
            if (refundLines.length) {
                return refundLines[0].externalTipTransactionId;
            }
            return null;
        }

        updateTipConvenienceFee(sale: Model.Sale, registerContext, chargedFee: number) {
            sale.tipConvenienceFee = chargedFee
            sale.tipConvenienceFeeRefund = this.getRefundTipConvenienceFee(sale);
            registerContext.updateTotal(sale);
            registerContext.recalculateChangeDue(sale);
        }

        getRefundTipConvenienceFee(sale: Model.Sale) {
            return _(sale.tipLines).chain()
                .filter(tipLine => {
                    return tipLine.isRefund && !!tipLine.externalTipTransactionId
                })
                .reduce((sum, tipLine) => this.roundingService.addNormalizedNumbers(sum, tipLine.convenienceFee || 0), 0)
                .value();
        }

        public isSaleTippyEligible(sale: Model.Sale, unauthorizedConfig: IUnauthorizedConfig = undefined): ng.IPromise<boolean> {
            if (this.hasExternalTip(sale, true)) {
                return this.$q.resolve(true);
            }

            return this.getTippyEmployeeMappings(unauthorizedConfig).then(mappings => {
                const eligibleTipLines = _(sale.tipLines).filter(tipLine => !tipLine.isRefund);
                return _(eligibleTipLines).any(tipLine => !!_(mappings).findWhere({ meevoEmployeeId: tipLine.employeeId }));
            });
        }

        private getCustomErrorMessage(errorMessage, unauthorizedConfig) {
            let message = this.$translate.instant(errorMessage);
            if (unauthorizedConfig.additionalMessage) {
                message += `<br/><br/>${this.$translate.instant(unauthorizedConfig.additionalMessage)}`;
            }
            return message;
        }

        static factory(): Function {
            let svc = (
                dataService,
                $q,
                busyService,
                pushMessageHandlerService,
                securityService,
                roundingService,
                $translate,
                identityService
            ) => new TippyService(
                dataService,
                $q,
                busyService,
                pushMessageHandlerService,
                securityService,
                roundingService,
                $translate,
                identityService
            );

            svc.$inject = [
                "meevo.components.common.tippyDataService",
                "$q",
                "meevo.components.modal.ModalService",
                "meevo.components.common.meevoPushMessageHandlerService",
                "meevo.components.common.SecurityService",
                "meevo.components.common.RoundingService",
                "$translate",
                "meevo.components.common.IdentityService"
            ];

            return svc;
        }
    }

    angular.module("meevo")
        .factory("meevo.plugins.tippy.tippyService", TippyService.factory());
}